"use client";

import Link from "next/link";

import Logo from "@/components/Logo";
import { IClosedInlineWidget } from "@/components/common/IClosedInlineWidget";
import { useLocalePath } from "@/hooks/useLocalePath";
import { Icon, ICON_TYPE } from "@/components/library/Icon";
import { FloatingIcon } from "@/components/redesign/FloatingIcon";
import { getAiModelBrand } from "@/constants/aiModelBrands";
import {
  GPT_CTA_ICON_FLOAT_DELAYS,
  getGptCtaIconPosition,
} from "@/constants/gptCtaBackgroundIcons";
import { BookDemoButton } from "@/puck/BookDemoButton";
import { useBlockAnchorId } from "@/puck/blockAnchors";
import {
  DurationBadge,
  PresenterAvatars,
  SchedulerCardFrame,
} from "@/puck/landing/schedulerCard";
import type {
  LpBookDemoBlockProps,
  LpFooterBlockProps,
  LpGptBottomCtaBlockProps,
} from "@/puck/types";
import { buildIClosedSchedulerUrl } from "@/utils/iclosed";

export function LpBookDemoBlock({
  eyebrow,
  titleLead,
  titleAccent,
  subtitle,
  iclosedEventUrl,
  iclosedEventTitle,
  benefits,
  layout = "embed",
  schedulerTabs = [],
  poweredByLabel = "",
  eventTitle = "",
  eventDescription = "",
  durationLabel = "",
  presentersLabel = "",
  presentersNames = "",
  presenters = [],
}: Readonly<LpBookDemoBlockProps>) {
  const schedulerUrl = buildIClosedSchedulerUrl({}, iclosedEventUrl);
  const bookDemoId = useBlockAnchorId("book-demo");
  const schedulerId = useBlockAnchorId("book-demo-scheduler");
  const isSchedulerLayout = layout === "scheduler";
  const hasPresenters =
    presenters.length > 0 || Boolean(presentersLabel) || Boolean(presentersNames);

  const widget = (
    <IClosedInlineWidget
      key={schedulerUrl}
      url={schedulerUrl}
      title={iclosedEventTitle}
    />
  );

  return (
    <section id={bookDemoId} className="w-full bg-gail-primary px-4 py-20 md:py-28">
      <div className="mx-auto w-full max-w-5xl">
        <div className="mx-auto max-w-2xl text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-white">
            {eyebrow}
          </p>
          <h2 className="mt-5 font-monument text-4xl font-bold leading-[1.08] tracking-tight text-white md:text-5xl">
            {titleLead} <span className="text-gail-sky">{titleAccent}</span>
          </h2>
          <p className="mx-auto mt-5 max-w-xl font-monument text-base leading-relaxed text-white md:text-lg">
            {subtitle}
          </p>
        </div>

        {isSchedulerLayout ? (
          <div
            id={schedulerId}
            className="mx-auto mt-10 w-full max-w-5xl scroll-mt-24"
          >
            <SchedulerCardFrame tabs={schedulerTabs} poweredByLabel={poweredByLabel}>
              <div className="grid gap-8 px-5 py-6 md:grid-cols-[minmax(0,4fr)_minmax(0,5fr)] md:gap-10 md:px-7 md:py-8">
                <div className="flex flex-col items-start gap-5">
                  {eventTitle && (
                    <h3 className="font-monument text-2xl font-bold tracking-tight text-black md:text-3xl">
                      {eventTitle}
                    </h3>
                  )}
                  {eventDescription && (
                    <p className="font-monument text-base leading-relaxed text-gail-gray">
                      {eventDescription}
                    </p>
                  )}
                  {durationLabel && <DurationBadge label={durationLabel} />}
                  {benefits.length > 0 && (
                    <ul className="flex flex-col gap-3">
                      {benefits.map((benefit, index) => (
                        <li
                          key={`benefit-${index}`}
                          className="flex items-center gap-2.5 font-monument text-sm text-black"
                        >
                          <Icon
                            icon="check"
                            type={ICON_TYPE.SOLID}
                            className="h-3.5 w-3.5 shrink-0 text-gail-primary"
                          />
                          {benefit.text}
                        </li>
                      ))}
                    </ul>
                  )}
                  {hasPresenters && (
                    <>
                      <hr className="w-full border-black/10" />
                      <PresenterAvatars
                        label={presentersLabel}
                        names={presentersNames}
                        presenters={presenters}
                      />
                    </>
                  )}
                </div>
                {widget}
              </div>
            </SchedulerCardFrame>
          </div>
        ) : (
          <>
            <div
              id={schedulerId}
              className="mx-auto mt-10 w-full max-w-5xl scroll-mt-24 rounded-2xl border border-black/10 bg-white px-3 pb-5 pt-4 shadow-[0_30px_70px_-40px_rgba(0,0,0,0.45)] md:px-4"
            >
              {widget}
            </div>

            <ul className="mx-auto mt-8 flex max-w-3xl flex-col flex-wrap items-center justify-center gap-3 sm:flex-row sm:gap-x-8">
              {benefits.map((benefit, index) => (
                <li
                  key={`benefit-${index}`}
                  className="flex items-center gap-2.5 font-monument text-sm text-white"
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-gail-primary">
                    <Icon icon="check" type={ICON_TYPE.SOLID} className="h-2.5 w-2.5" />
                  </span>
                  {benefit.text}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}

export function LpFooterBlock({
  copyright,
  privacyLabel,
  termsLabel,
}: Readonly<LpFooterBlockProps>) {
  const lp = useLocalePath();
  const year = new Date().getFullYear();

  return (
    <footer className="w-full border-t border-black/10 bg-marketing-surface font-monument">
      <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-6 px-4 py-16 md:flex-row md:justify-between md:py-20">
        <div className="flex items-center gap-3">
          <Logo className="h-5" />
          <span className="text-xs text-gail-gray">
            {copyright.replace("{{year}}", String(year))}
          </span>
        </div>

        <nav className="flex items-center gap-6">
          <Link
            href={lp("/privacy-policy")}
            className="text-sm text-gail-gray transition-colors hover:text-black"
          >
            {privacyLabel}
          </Link>
          <Link
            href={lp("/terms")}
            className="text-sm text-gail-gray transition-colors hover:text-black"
          >
            {termsLabel}
          </Link>
        </nav>
      </div>
    </footer>
  );
}

export function LpGptBottomCtaBlock({
  eyebrow,
  title,
  titleAccent,
  titleTrail,
  subtitle,
  ctaLabel,
  backgroundIcons = [],
}: Readonly<LpGptBottomCtaBlockProps>) {
  return (
    <section className="relative w-full overflow-hidden bg-gail-primary px-4 py-20 md:py-24">
      {backgroundIcons.length > 0 && (
        <div aria-hidden className="pointer-events-none absolute inset-0">
          {backgroundIcons.map((icon, index) => {
            const brand = getAiModelBrand(icon.brandId);
            return (
              // Placement lives on a plain wrapper: `.floating-icon` sets
              // `position: relative`, which would otherwise override an
              // `absolute` utility on the icon itself and break the anchors.
              <span
                key={`${icon.brandId}-${icon.position}-${index}`}
                className={`absolute ${getGptCtaIconPosition(icon.position)}`}
              >
                <FloatingIcon
                  src={brand.src}
                  alt=""
                  width={brand.width}
                  height={brand.height}
                  intensity="faint"
                  size={icon.size}
                  delay={
                    GPT_CTA_ICON_FLOAT_DELAYS[
                      index % GPT_CTA_ICON_FLOAT_DELAYS.length
                    ]
                  }
                />
              </span>
            );
          })}
        </div>
      )}

      <div className="relative mx-auto flex w-full max-w-3xl flex-col items-center text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-white">
          {eyebrow}
        </p>
        <h2 className="mt-5 font-monument text-4xl font-bold leading-[1.08] tracking-tight text-white md:text-5xl">
          {title}
          {/* Optional parts keep their leading space INSIDE the wrapper so
              hiding the wrapper removes the gap too. `puck-inline-optional`
              collapses the wrapper in the Studio canvas while the inline
              (contentEditable) field is empty — in the editor Puck swaps the
              prop for an always-truthy editable element, so the string check
              alone can't hide it there. */}
          {titleAccent ? (
            <span className="puck-inline-optional text-gail-sky">
              {" "}
              {titleAccent}
            </span>
          ) : null}
          {titleTrail ? (
            <span className="puck-inline-optional"> {titleTrail}</span>
          ) : null}
        </h2>
        <p className="mt-5 max-w-xl font-monument text-base leading-relaxed text-white md:text-lg">
          {subtitle}
        </p>
        <BookDemoButton
          href="#book-demo"
          className="mt-8 bg-white! px-7 py-3.5 text-base text-gail-primary! hover:bg-white/90! active:bg-white/90!"
          iconAfter={
            <Icon
              icon="arrow-right"
              type={ICON_TYPE.SOLID}
              className="size-4 shrink-0"
            />
          }
        >
          {ctaLabel}
        </BookDemoButton>
      </div>
    </section>
  );
}
